// Webhooks.tsx
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Collapse,
  Stack,
  Paper,
  Typography,
  Divider,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import {
  Trash2 as DeleteIcon,
  Copy as ContentCopyIcon,
  Plus as AddIcon,
  X as CloseIcon,
  Webhook as WebhookIcon,
  Webhook as WebhookOutlinedIcon,
  Info as InfoIcon,
} from 'lucide-mui';
import {
  addWebhookUrlThunk,
  removeWebhookUrlThunk,
} from '../../team/teamSlice';
import { RootState } from '../../../reducers';
import { Webhook, getWebhookType, WEBHOOK_PROVIDERS } from './IntegrationTypes';
import { useLimits } from '../../plan/hooks/limits';
import { ToggleSetting } from '../common';

const SNACKBAR_DURATION = 4000;

const SAMPLE_WEBHOOK_PAYLOAD = `
\`\`\`json
{
  "event": "transcriptionComplete",
  "file": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "folderId": "987fde65-4321-abcd-ef98-765432198000",
    "summary": "[00:00:00] Meeting begins with introductions\\n[00:01:30] Discussion of Q4 goals\\n[00:15:45] Action items review\\n[00:30:00] Meeting concludes",
    "url": "https://store.screenapp.io/vid/636adab85ea1ce00121690a9/7578091c-1c46-4c45-806e-02ca4351a2f9.mp4",
    "owner": {
      "name": "John Smith",
      "picture": "https://example.com/profiles/john-smith.jpg"
    },
    "userPromptResponses": {}
  }
}
\`\`\`
`;

const Webhooks = () => {
  const dispatch = useDispatch();
  const { team } = useSelector((state: RootState) => state);
  const { integrations = { webhooks: [] }, teamId = '' } =
    team.selectedTeam || {};
  const guardLimits = useLimits();

  const [formState, setFormState] = useState({
    newWebhookUrl: '',
    newWebhookName: '',
    snackbarOpen: false,
    snackbarMessage: '',
    deleteDialogOpen: false,
    webhookToDelete: null as Webhook | null,
    showAddWebhook: false,
    webhookLearnMoreOpen: false,
  });

  const updateFormState = (updates: Partial<typeof formState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  };

  const handleAddWebhook = () => {
    guardLimits('integrations', () => {
      const { newWebhookUrl, newWebhookName } = formState;

      if (!newWebhookUrl || !newWebhookName) {
        updateFormState({
          snackbarMessage: 'Please fill in both name and URL fields',
          snackbarOpen: true,
        });
        return;
      }

      if (!newWebhookUrl.startsWith('https://')) {
        updateFormState({
          snackbarMessage: 'Webhook URL must start with https://',
          snackbarOpen: true,
        });
        return;
      }

      if (!teamId) return;

      dispatch(
        addWebhookUrlThunk({ teamId, name: newWebhookName, url: newWebhookUrl })
      );
      updateFormState({
        newWebhookUrl: '',
        newWebhookName: '',
        snackbarMessage: 'Webhook added successfully',
        snackbarOpen: true,
        showAddWebhook: false,
      });
    });
  };

  const handleRemoveWebhook = (url: string, name: string) => {
    updateFormState({
      webhookToDelete: { url, name },
      deleteDialogOpen: true,
    });
  };

  const handleConfirmDelete = () => {
    const { webhookToDelete } = formState;
    if (!teamId || !webhookToDelete) return;

    dispatch(removeWebhookUrlThunk({ teamId, url: webhookToDelete.url }));
    updateFormState({
      snackbarMessage: 'Webhook removed successfully',
      snackbarOpen: true,
      deleteDialogOpen: false,
      webhookToDelete: null,
    });
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    updateFormState({
      snackbarMessage: `${label} copied to clipboard`,
      snackbarOpen: true,
    });
  };

  const toggleState = (key: keyof typeof formState) => {
    updateFormState({ [key]: !formState[key] });
  };

  const handleClose = (key: keyof typeof formState) => {
    updateFormState({ [key]: false });
  };

  return (
    <Box sx={{ mb: 3 }}>
      <ToggleSetting
        checked={false}
        onChange={() => {}}
        label="Learn More"
        description="Understand how to use webhooks to receive notifications"
        icon={<InfoIcon />}
        rightElement={
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => updateFormState({ webhookLearnMoreOpen: true })}
          >
            Learn More
          </Button>
        }
      />

      {integrations.webhooks.length > 0 ? (
        <Paper variant="outlined" sx={{ borderColor: 'divider', mt: 2 }}>
          <List disablePadding>
            {integrations.webhooks.map((webhook, index) => (
              <React.Fragment key={webhook.url}>
                <ListItem
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Copy webhook URL">
                        <IconButton
                          onClick={() => handleCopy(webhook.url, 'Webhook URL')}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete webhook">
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() =>
                            handleRemoveWebhook(webhook.url, webhook.name)
                          }
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {(() => {
                          const webhookType = getWebhookType(webhook.url);
                          const provider = WEBHOOK_PROVIDERS.find(
                            (p) => p.id === webhookType
                          );

                          return provider?.logoPath ? (
                            <Box
                              component="img"
                              src={provider.logoPath}
                              sx={{ width: 20, height: 20 }}
                              alt={provider.name}
                            />
                          ) : (
                            <WebhookIcon
                              sx={{
                                width: 20,
                                height: 20,
                                color: 'primary.main',
                              }}
                            />
                          );
                        })()}
                        <Typography variant="subtitle1" noWrap>
                          {webhook.name}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          wordBreak: 'break-all',
                          pr: 16,
                        }}
                      >
                        {webhook.url}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < integrations.webhooks.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            mt: 2,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <WebhookOutlinedIcon
            sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
          />
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No webhooks configured yet
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          startIcon={formState.showAddWebhook ? <CloseIcon /> : <AddIcon />}
          onClick={() => toggleState('showAddWebhook')}
          size="small"
          variant={formState.showAddWebhook ? 'outlined' : 'contained'}
          color={formState.showAddWebhook ? 'secondary' : 'primary'}
        >
          {formState.showAddWebhook ? 'Cancel' : 'Add Webhook'}
        </Button>
      </Box>

      <Collapse in={formState.showAddWebhook}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <TextField
                label="Webhook Name"
                placeholder="e.g., Production Server"
                variant="outlined"
                value={formState.newWebhookName}
                onChange={(e) =>
                  updateFormState({ newWebhookName: e.target.value })
                }
                fullWidth
                helperText="A descriptive name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Webhook URL"
                placeholder="https://your-server.com/webhook"
                variant="outlined"
                value={formState.newWebhookUrl}
                onChange={(e) =>
                  updateFormState({ newWebhookUrl: e.target.value })
                }
                fullWidth
                helperText="Must be a secure HTTPS endpoint"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddWebhook}
                fullWidth
                disabled={!formState.newWebhookUrl || !formState.newWebhookName}
                sx={{ height: { xs: '40px', md: '56px' } }}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* Snackbar for notifications */}
      <Snackbar
        open={formState.snackbarOpen}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={() => handleClose('snackbarOpen')}
        message={formState.snackbarMessage}
      />

      {/* Dialog for deleting a webhook */}
      <Dialog
        open={formState.deleteDialogOpen}
        onClose={() => handleClose('deleteDialogOpen')}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Webhook</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the webhook &ldquo;
            {formState.webhookToDelete?.name}&rdquo;? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose('deleteDialogOpen')}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for learning more about webhooks */}
      <Dialog
        open={formState.webhookLearnMoreOpen}
        onClose={() => handleClose('webhookLearnMoreOpen')}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Understanding Webhooks</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography paragraph>
              Webhooks allow you to receive real-time notifications when
              transcription processing is complete. Each webhook delivers a POST
              request containing file details, transcription text, and
              AI-generated summaries.
            </Typography>

            <Typography paragraph>
              <strong>Note:</strong> Webhooks will only trigger if you
              record/upload the file to a folder other than &ldquo;My
              Recordings&rdquo; or if you move a file from &ldquo;My
              Recordings&rdquo; to another folder.
            </Typography>

            <Typography variant="h6" gutterBottom>
              Sample Webhook Payload
            </Typography>

            <Box
              sx={{
                bgcolor: 'grey.100',
                p: 2,
                borderRadius: 1,
                mb: 2,
                overflowX: 'auto',
              }}
            >
              <ReactMarkdown>{SAMPLE_WEBHOOK_PAYLOAD}</ReactMarkdown>
            </Box>

            <Typography variant="h6" gutterBottom>
              Common Integration Use Cases
            </Typography>

            <Typography variant="subtitle1" gutterBottom>
              Content Management
            </Typography>
            <ul>
              <li>Auto-publish transcripts to your CMS</li>
              <li>Update documentation in Notion or Confluence</li>
              <li>Archive meeting summaries in your knowledge base</li>
            </ul>

            <Typography variant="subtitle1" gutterBottom>
              Team Collaboration
            </Typography>
            <ul>
              <li>Create tasks from meeting action items</li>
              <li>Share summaries in Slack or Microsoft Teams</li>
              <li>Update project status in task management tools</li>
            </ul>

            <Typography variant="subtitle1" gutterBottom>
              Custom Processing
            </Typography>
            <ul>
              <li>Trigger custom workflows with transcription data</li>
              <li>Feed content into your analytics pipeline</li>
              <li>Generate custom reports or notifications</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleClose('webhookLearnMoreOpen')}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Webhooks;
